import { Alert } from '@/components/ui/alert';
import { useCashRecalcPreview } from '@/hooks/queries/useFinanceReports';
import { answered, reportsUnreadyMessage } from '@/lib/reportsRead';

interface CashRecalcRippleNoticeProps {
  /**
   * A date inside the period being edited (its period end). Any cash edit here
   * ripples forward to every later editable period's opening balance.
   */
  editDate: string;
}

/**
 * Issue #643 — the warn-before notice for a cross-period cash recalc. Shown on an
 * editable report when a cash edit here would re-seed later periods' opening
 * balances. Alert-don't-block: it names the ripple so the treasurer understands a
 * change carries forward; it never gates the edit (the recalc runs synchronously
 * with the confirmed mutation). Renders nothing when there is no downstream ripple
 * (e.g. editing the latest period).
 */
export function CashRecalcRippleNotice({ editDate }: CashRecalcRippleNoticeProps) {
  const read = useCashRecalcPreview(editDate);
  const data = answered(read);
  const unready = reportsUnreadyMessage('check of later periods this edit changes', read);

  const recalculated = data?.recalculatedPeriods ?? [];
  const filed = data?.filedPeriodsNeedingPortalAmendment ?? [];
  if (unready) {
    return (
      <Alert severity="warning" className="mt-4" data-testid="cash-recalc-ripple-unready">
        <p>{unready}</p>
      </Alert>
    );
  }
  if (recalculated.length === 0 && filed.length === 0) return null;

  return (
    <Alert severity="warning" className="mt-4" data-testid="cash-recalc-ripple-notice">
      {recalculated.length > 0 && (
        <p>
          Editing this period recalculates the opening balance of{' '}
          {recalculated.length} later {recalculated.length === 1 ? 'period' : 'periods'}:{' '}
          <span className="font-medium">{recalculated.map((p) => p.name).join(', ')}</span>.
        </p>
      )}
      {filed.length > 0 && (
        <p className={recalculated.length > 0 ? 'mt-1' : undefined}>
          {filed.length} already-filed {filed.length === 1 ? 'period' : 'periods'} (
          <span className="font-medium">{filed.map((p) => p.name).join(', ')}</span>) would no
          longer match — that is a portal amendment, handled in the state portal, not changed here.
        </p>
      )}
    </Alert>
  );
}
